import { useAppDisplayModeContext } from '@archipro-design/aria';
import { CarouselSection } from '@rocco/ui/layout/components/CarouselSection';
import type {
    TileableModel,
    TileViewModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { CategoryPageTileWithAdminActions } from '~/ui-rocco/pages/category-page/CategoryPageTileWithAdminActions';
import { getPinType } from '~/modules/moodboards/utils/tile-pin-type';
import { useCategoryRevamp } from '@modules/category/hook/use-category-revamp';

/**
 * Renders a carousel section displaying recently viewed items.
 * Adapts tile aspect ratio based on mobile/desktop view; with the category
 * revamp active the mixed-type carousel uses the uniform portrait card so
 * every item sits at the same height.
 */
export const SearchPageRecentlyViewed = ({
    recentlyViewed,
}: {
    recentlyViewed: TileableModel[];
}) => {
    const { isInDesignBoard } = useHandleFollow();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const categoryRevamp = useCategoryRevamp();

    if (recentlyViewed.length === 0) {
        return null;
    }

    const recentlyViewedTiles: TileViewModel<TileableModel>[] =
        recentlyViewed.map((item: TileableModel) => {
            return {
                aspectRatio: categoryRevamp
                    ? ('portrait' as const)
                    : isMobile
                      ? ('default' as const)
                      : ('square' as const),
                model: item,
            };
        });

    return (
        <CarouselSection
            title="Recently viewed"
            size={isMobile ? 'mobileSearchResults' : 'recentlyViewed'}
        >
            {recentlyViewedTiles.map((tile, index) => (
                <CategoryPageTileWithAdminActions
                    key={`recentlyViewedTiles-${index}`}
                    tile={{
                        ...tile,
                        compact: isMobile,
                        isFollowed: isInDesignBoard(
                            tile.model.id,
                            getPinType(tile.model),
                        ),
                    }}
                    showTypeBadge
                />
            ))}
        </CarouselSection>
    );
};
